import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@components/shadcn-ui/card";
import { User } from "lucide-react";
import { Loader2 } from "lucide-react";
import { addressService, AddressResponse } from "../service/addressService";

export interface ReferenceDisplayData {
  addressId: string;
  relationToApplicant: string;
  personalDetails: {
    firstName: string;
    lastName: string;
    phoneNumber: string;
  };
}

interface ReferenceDisplayProps {
  applicationId: string;
  references?: ReferenceDisplayData[];
}

export const ReferenceDisplay: React.FC<ReferenceDisplayProps> = ({
  applicationId,
  references: initialReferences,
}) => {
  const [references, setReferences] = useState<ReferenceDisplayData[]>(initialReferences || []);
  const [isLoading, setIsLoading] = useState(!initialReferences);
  const [addressesMap, setAddressesMap] = useState<Record<string, AddressResponse>>({});
  const [isLoadingAddresses, setIsLoadingAddresses] = useState(false);

  const fetchAddresses = useCallback(async (refs: ReferenceDisplayData[]) => {
    if (!applicationId || refs.length === 0) return;
    
    setIsLoadingAddresses(true);
    try {
      // Get unique address IDs
      const addressIds = [...new Set(refs.map(ref => ref.addressId))];
      
      // Create a map of address ID to address data
      const addressMap: Record<string, AddressResponse> = {};
      
      // Fetch each address
      for (const addressId of addressIds) {
        try {
          const address = await addressService.getAddressById(applicationId, addressId);
          if (address) {
            addressMap[addressId] = address;
          }
        } catch (error) {
          console.error(`Error fetching address ${addressId}:`, error);
        }
      }
      
      setAddressesMap(addressMap);
    } catch (error) {
      console.error("Error fetching addresses:", error);
    } finally {
      setIsLoadingAddresses(false);
    }
  }, [applicationId]);

  useEffect(() => {
    if (initialReferences) {
      setReferences(initialReferences);
      setIsLoading(false);
      
      // Fetch addresses for all references
      fetchAddresses(initialReferences);
    } else if (applicationId) {
      // If we need to fetch references from API, we would do it here
      // For now, we'll just use the initialReferences prop
      setIsLoading(false);
    }
  }, [applicationId, initialReferences, fetchAddresses]);

  // Format address for display
  const formatAddress = (addressId: string) => {
    const address = addressesMap[addressId];
    if (!address) return "Loading address...";
    
    const parts = [
      address.line1,
      address.line2,
      address.landmark,
      address.city,
      address.state,
      address.postalCode !== undefined && address.postalCode !== null ? String(address.postalCode) : '',
      address.country
    ].filter(Boolean);
    
    return parts.join(", ");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2">Loading references...</span>
      </div>
    );
  }

  if (references.length === 0) {
    return (
      <div className="text-center p-8 border border-dashed rounded-md">
        <p className="text-gray-500">No references found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {references.map((reference, index) => (
        <Card key={index} className="overflow-hidden">
          <CardContent className="p-0">
            <div className="bg-gray-50 p-3 flex items-center">
              <div className="bg-primary/10 p-2 rounded-full">
                <User className="h-5 w-5 text-primary" />
              </div>
              <h3 className="ml-2 font-medium">Reference #{index + 1}</h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div>
                  <p className="text-gray-500">Name</p>
                  <p className="font-medium">
                    {reference.personalDetails.firstName} {reference.personalDetails.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Relation</p>
                  <p className="font-medium">{reference.relationToApplicant}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone Number</p>
                  <p className="font-medium">{reference.personalDetails.phoneNumber}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Address</p>
                  {isLoadingAddresses ? (
                    <div className="flex items-center">
                      <Loader2 className="h-3 w-3 animate-spin mr-2" />
                      <span className="text-sm">Loading address...</span>
                    </div>
                  ) : addressesMap[reference.addressId] ? (
                    <p className="font-medium text-sm">{formatAddress(reference.addressId)}</p>
                  ) : (
                    <p className="text-xs text-gray-400">Address ID: {reference.addressId}</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
